import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useState } from 'react';

import { NAV_GROUPS } from '@/config/navigation';
import { useOperator } from '@/features/auth/operator-context';
import { ENTITY_KIND_STYLE, useRecentEntities } from '@/features/recents/recent-entities';
import {
  detectQueryKind,
  QUERY_KIND_HINT,
  useGlobalSearch,
  type SearchHit,
} from '@/features/search/search';

const GROUP_HEADING_CLASS =
  'mb-1 [&_[cmdk-group-heading]]:font-head [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.16em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-tx3';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Global search palette (⌘K) — the primary navigation route, and on mobile a
 * full-screen view rather than a dialog.
 *
 * Searches leagues, users, squads, communities and shop items in one field, and
 * accepts a raw ObjectId, invite code, username, email or phone number.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="motion-safe:animate-mesh-fade fixed inset-0 z-120"
          style={{ background: 'var(--scrim)' }}
        />
        <Dialog.Content
          className="border-bd bg-card fixed inset-0 z-130 flex flex-col sm:inset-x-0 sm:top-[10vh] sm:bottom-auto sm:mx-auto sm:max-h-[70vh] sm:w-[92vw] sm:max-w-[620px] sm:rounded-xl sm:border sm:shadow-[var(--shadow-lg)]"
          aria-describedby={undefined}
        >
          <VisuallyHidden>
            <Dialog.Title>Global search</Dialog.Title>
          </VisuallyHidden>
          {/* Body lives inside the Portal, which unmounts on close. That is what
              resets the query between openings — an operator reaching for search
              mid-incident must never land on a stale term. */}
          <PaletteBody onClose={() => onOpenChange(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const [term, setTerm] = useState('');
  const navigate = useNavigate();
  const recents = useRecentEntities();
  const { can } = useOperator();

  const { data, isFetching, isError } = useGlobalSearch(term, true);
  const trimmed = term.trim();
  const hint = trimmed.length >= 2 ? QUERY_KIND_HINT[detectQueryKind(term)] : null;
  const showDefaults = trimmed.length < 2;

  function go(to: string) {
    onClose();
    void navigate({ to });
  }

  const sections = data?.groups.filter((group) => group.hits.length > 0) ?? [];

  return (
    <Command shouldFilter={false} loop className="flex min-h-0 flex-1 flex-col">
      <div className="border-bd flex flex-none items-center gap-2.5 border-b px-3.5 py-3">
        <Search size={15} className="text-tx3 flex-none" aria-hidden />
        <Command.Input
          value={term}
          onValueChange={setTerm}
          // The palette exists to be typed into the instant it opens; focusing it
          // is the entire point of a command palette, and it only mounts in
          // response to explicit user intent (⌘K or tapping search).
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          placeholder="Search leagues, users, squads, ObjectIds, invite codes…"
          className="text-tx placeholder:text-tx3 min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
        {isFetching && (
          <span
            aria-hidden
            className="border-bd border-t-info motion-safe:animate-mesh-spin size-3 flex-none rounded-full border-2"
          />
        )}
      </div>

      {hint && (
        <div className="border-bd bg-elev text-tx3 flex-none border-b px-3.5 py-1.5 font-mono text-[10.5px]">
          {hint}
        </div>
      )}

      <Command.List className="min-h-0 flex-1 overflow-y-auto p-2">
        {isError && (
          <div className="text-err-soft px-2 py-6 text-center text-[13px]">
            Search is unavailable. Check that mesh-admin-api is reachable.
          </div>
        )}

        {!isError && (
          <Command.Empty className="text-tx3 px-2 py-6 text-center text-[13px]">
            {showDefaults
              ? 'Type at least two characters, or paste an ObjectId.'
              : isFetching
                ? 'Searching…'
                : 'No matches.'}
          </Command.Empty>
        )}

        {data?.exact && (
          <Command.Group heading="Exact match" className={GROUP_HEADING_CLASS}>
            <HitRow hit={data.exact} onSelect={go} />
          </Command.Group>
        )}

        {sections.map((group) => (
          <Command.Group key={group.kind} heading={group.label} className={GROUP_HEADING_CLASS}>
            {group.hits.map((hit) => (
              <HitRow key={`${hit.kind}:${hit.id}`} hit={hit} onSelect={go} />
            ))}
          </Command.Group>
        ))}

        {showDefaults && recents.length > 0 && (
          <Command.Group heading="Recent" className={GROUP_HEADING_CLASS}>
            {recents.map((recent) => (
              <HitRow
                key={`recent:${recent.kind}:${recent.id}`}
                hit={{
                  kind: recent.kind,
                  id: recent.id,
                  title: recent.name,
                  to: recent.to,
                }}
                onSelect={go}
              />
            ))}
          </Command.Group>
        )}

        {showDefaults && (
          <Command.Group heading="Go to" className={GROUP_HEADING_CLASS}>
            {NAV_GROUPS.flatMap((group) => group.items)
              .filter((item) => can(item.capability))
              .map((item) => (
                <Command.Item
                  key={item.to}
                  value={`nav:${item.label}`}
                  onSelect={() => go(item.to)}
                  className="text-tx2 data-[selected=true]:bg-elev data-[selected=true]:text-tx flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-[13px]"
                >
                  <item.icon size={14} className="text-tx3 flex-none" aria-hidden />
                  {item.label}
                </Command.Item>
              ))}
          </Command.Group>
        )}
      </Command.List>

      <div className="border-bd text-tx3 flex flex-none items-center gap-3 border-t px-3.5 py-2 font-mono text-[10px]">
        <KeyHint keys="↑↓" label="navigate" />
        <KeyHint keys="↵" label="open" />
        <KeyHint keys="esc" label="close" />
      </div>
    </Command>
  );
}

function HitRow({ hit, onSelect }: { hit: SearchHit; onSelect: (to: string) => void }) {
  const style = ENTITY_KIND_STYLE[hit.kind];

  return (
    <Command.Item
      value={`${hit.kind}:${hit.id}:${hit.title}`}
      onSelect={() => onSelect(hit.to)}
      className="data-[selected=true]:bg-elev flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2"
    >
      <span
        aria-hidden
        className="grid size-5 flex-none place-items-center rounded font-mono text-[9px] font-bold"
        style={{ background: style.tint, color: style.tintFg }}
      >
        {style.tag}
      </span>
      <span className="text-tx min-w-0 flex-1 truncate text-[13px]">{hit.title}</span>
      {hit.subtitle && (
        <span className="text-tx3 flex-none font-mono text-[10.5px]">{hit.subtitle}</span>
      )}
    </Command.Item>
  );
}

function KeyHint({ keys, label }: { keys: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <kbd className="border-bd-s bg-high text-tx2 rounded border px-1.5 py-px text-[10px]">
        {keys}
      </kbd>
      {label}
    </span>
  );
}
